import React, { useEffect, useMemo, useState } from "react";
import { reports as reportsApi, hotels } from "../api/endpoints";
import { DataTableShell, FilterToolbar, MetricStrip, PageHeader, SectionCard } from "../components/Workspace";
import { triggerDownload } from "../utils/download";
import type { ManagementCompany, ManagementDashboardOut, User } from "../types";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MIN_REPORT_YEAR = 2013;

interface Props {
  user: User;
}

interface BarChartPoint {
  label: string;
  value: number;
}

interface SeriesChartPoint {
  label: string;
  value: number;
}

interface SeriesConfig {
  label: string;
  color: string;
  points: SeriesChartPoint[];
}

interface BandPoint {
  label: string;
  green: number;
  yellow: number;
  red: number;
}

const chartGrid = [0, 0.25, 0.5, 0.75, 1];

function niceMax(values: number[], fallback = 10) {
  const maxValue = Math.max(...values, fallback);
  if (maxValue <= 10) return Math.ceil(maxValue);
  return Math.ceil(maxValue / 10) * 10;
}

function yForValue(value: number, maxValue: number, height: number, top: number) {
  return top + (1 - value / Math.max(maxValue, 1)) * height;
}

function formatDashboardDate(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.split("T")[0] || value;
  return date.toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "numeric" });
}

function StatsPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <SectionCard title={title}>
      <div className="h-[18rem]">{children}</div>
    </SectionCard>
  );
}

function SimpleBarChart({ data, color, maxValue, valueSuffix = "" }: { data: BarChartPoint[]; color: string; maxValue?: number; valueSuffix?: string }) {
  const width = 640;
  const height = 260;
  const pad = { top: 26, right: 18, bottom: 38, left: 42 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const chartMax = maxValue ?? niceMax(data.map((point) => point.value));
  const slot = plotWidth / Math.max(data.length, 1);
  const barWidth = Math.min(72, slot * 0.64);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full" role="img">
      {chartGrid.map((line) => {
        const y = pad.top + line * plotHeight;
        const value = Math.round(chartMax * (1 - line));
        return (
          <g key={line}>
            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={pad.left - 10} y={y + 4} textAnchor="end" className="fill-slate-500 text-[11px] font-semibold">
              {value}
            </text>
          </g>
        );
      })}
      {data.map((point, index) => {
        const x = pad.left + index * slot + (slot - barWidth) / 2;
        const y = yForValue(point.value, chartMax, plotHeight, pad.top);
        const barHeight = pad.top + plotHeight - y;
        return (
          <g key={point.label}>
            <rect x={x} y={y} width={barWidth} height={barHeight} fill={color} opacity="0.88" rx="2" />
            <text x={x + barWidth / 2} y={height - 12} textAnchor="middle" className="fill-slate-600 text-[12px] font-semibold">
              {point.label}
            </text>
            <text x={x + barWidth / 2} y={Math.max(y - 7, 14)} textAnchor="middle" className="fill-slate-700 text-[11px] font-semibold">
              {point.value.toFixed(valueSuffix ? 1 : 0)}{valueSuffix}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function MultiLineChart({ series }: { series: SeriesConfig[] }) {
  const width = 640;
  const height = 260;
  const pad = { top: 34, right: 18, bottom: 38, left: 42 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const labels = series[0]?.points.map((point) => point.label) ?? [];
  const allValues = series.flatMap((item) => item.points.map((point) => point.value));
  const chartMax = niceMax(allValues, 6);
  const xForIndex = (index: number) => pad.left + (labels.length <= 1 ? plotWidth / 2 : (index / (labels.length - 1)) * plotWidth);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full" role="img">
      <g>
        {series.map((item, index) => (
          <g key={item.label} transform={`translate(${pad.left + index * 98}, 14)`}>
            <rect width="10" height="10" fill={item.color} rx="2" />
            <text x="15" y="9" className="fill-slate-600 text-[11px] font-semibold">
              {item.label}
            </text>
          </g>
        ))}
      </g>
      {chartGrid.map((line) => {
        const y = pad.top + line * plotHeight;
        const value = Math.round(chartMax * (1 - line));
        return (
          <g key={line}>
            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={pad.left - 10} y={y + 4} textAnchor="end" className="fill-slate-500 text-[11px] font-semibold">
              {value}
            </text>
          </g>
        );
      })}
      {labels.map((label, index) => (
        <text key={label} x={xForIndex(index)} y={height - 12} textAnchor="middle" className="fill-slate-600 text-[12px] font-semibold">
          {label}
        </text>
      ))}
      {series.map((item) => {
        const path = item.points
          .map((point, index) => `${index === 0 ? "M" : "L"} ${xForIndex(index)} ${yForValue(point.value, chartMax, plotHeight, pad.top)}`)
          .join(" ");
        return (
          <g key={item.label}>
            <path d={path} fill="none" stroke={item.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {item.points.map((point, index) => (
              <circle
                key={`${item.label}-${point.label}`}
                cx={xForIndex(index)}
                cy={yForValue(point.value, chartMax, plotHeight, pad.top)}
                r="4"
                fill={item.color}
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function GroupedBandChart({ data }: { data: BandPoint[] }) {
  const width = 640;
  const height = 260;
  const pad = { top: 34, right: 18, bottom: 38, left: 42 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const chartMax = niceMax(data.flatMap((point) => [point.green, point.yellow, point.red]), 10);
  const slot = plotWidth / Math.max(data.length, 1);
  const groupWidth = Math.min(92, slot * 0.72);
  const barWidth = groupWidth / 3;
  const colors = { green: "#4ec1bd", yellow: "#f6c85f", red: "#fb5b7f" };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full" role="img">
      <g>
        {[
          ["Green", colors.green],
          ["Yellow", colors.yellow],
          ["Red", colors.red],
        ].map(([label, color], index) => (
          <g key={label} transform={`translate(${pad.left + 210 + index * 82}, 14)`}>
            <rect width="10" height="10" fill={color} rx="2" />
            <text x="15" y="9" className="fill-slate-600 text-[11px] font-semibold">
              {label}
            </text>
          </g>
        ))}
      </g>
      {chartGrid.map((line) => {
        const y = pad.top + line * plotHeight;
        const value = Math.round(chartMax * (1 - line));
        return (
          <g key={line}>
            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={pad.left - 10} y={y + 4} textAnchor="end" className="fill-slate-500 text-[11px] font-semibold">
              {value}
            </text>
          </g>
        );
      })}
      {data.map((point, index) => {
        const x = pad.left + index * slot + (slot - groupWidth) / 2;
        return (
          <g key={point.label}>
            {(["green", "yellow", "red"] as const).map((key, barIndex) => {
              const y = yForValue(point[key], chartMax, plotHeight, pad.top);
              return (
                <rect
                  key={key}
                  x={x + barIndex * barWidth}
                  y={y}
                  width={Math.max(barWidth - 2, 2)}
                  height={pad.top + plotHeight - y}
                  fill={colors[key]}
                  rx="2"
                />
              );
            })}
            <text x={x + groupWidth / 2} y={height - 12} textAnchor="middle" className="fill-slate-600 text-[12px] font-semibold">
              {point.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function ManagementDashboardPage({ user }: Props) {
  const isGuest = user.role?.name === "Guest";
  const isReportScoped = isGuest || Boolean(user.report_management_company_id);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState<number | "ytd">(new Date().getMonth() + 1);
  const [mcId, setMcId] = useState<number | undefined>(user.report_management_company_id || undefined);
  const [mcList, setMcList] = useState<ManagementCompany[]>([]);
  const [managementDashboard, setManagementDashboard] = useState<ManagementDashboardOut | null>(null);

  const params = useMemo(
    () => `year=${year}${month === "ytd" ? "" : `&month=${month}`}${mcId ? `&mc_id=${mcId}` : ""}`,
    [year, month, mcId]
  );
  const selectedMonthName = month === "ytd" ? "" : MONTH_NAMES[month - 1];
  const lastTrendRow = managementDashboard?.volume_by_month[managementDashboard.volume_by_month.length - 1];
  const trendEndMonth = lastTrendRow?.month_name || selectedMonthName || "Dec";
  const periodLabel = month === "ytd" ? `YTD ${year}` : `${selectedMonthName} ${year}`;
  const trendLabel = month === "ytd" ? `${year} YTD` : `${year} through ${selectedMonthName}`;
  const trendRangeLabel = `Jan to ${trendEndMonth} ${year}`;
  const availableYears = useMemo(
    () => Array.from({ length: currentYear - MIN_REPORT_YEAR + 2 }, (_, index) => MIN_REPORT_YEAR + index).reverse(),
    [currentYear]
  );

  useEffect(() => {
    hotels.managementCompanies.list("limit=500").then((res) => setMcList(res.items));
  }, []);

  useEffect(() => {
    reportsApi.managementDashboard(params).then(setManagementDashboard);
  }, [params]);

  const downloadCsv = async () => {
    try {
      await triggerDownload(reportsApi.exportUrl("management-dashboard", params));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Download failed");
    }
  };

  if (!managementDashboard) {
    return (
      <div className="surface-panel p-6 text-sm text-slate-500">
        Loading management dashboard...
      </div>
    );
  }

  const atRiskCount = managementDashboard.summary.yellow_count + managementDashboard.summary.red_count;
  const overallScoreData = managementDashboard.score_breakdown.map((row) => ({
    label: row.month_name,
    value: row.total_score,
  }));
  const bandData = managementDashboard.band_by_month.map((row) => ({
    label: row.month_name,
    green: row.green,
    yellow: row.yellow,
    red: row.red,
  }));
  const volumeData = managementDashboard.volume_by_month.map((row) => ({
    label: row.month_name,
    value: row.count,
  }));
  const componentSeries: SeriesConfig[] = [
    {
      label: `Admin(${managementDashboard.summary.administration_target})`,
      color: "#fb5b7f",
      points: managementDashboard.score_breakdown.map((row) => ({ label: row.month_name, value: row.administration })),
    },
    {
      label: `Hosp(${managementDashboard.summary.hospitality_target})`,
      color: "#3aa0e6",
      points: managementDashboard.score_breakdown.map((row) => ({ label: row.month_name, value: row.hospitality })),
    },
    {
      label: `Qual(${managementDashboard.summary.qualifying_target})`,
      color: "#4ec1bd",
      points: managementDashboard.score_breakdown.map((row) => ({ label: row.month_name, value: row.qualifying })),
    },
    {
      label: `Sell(${managementDashboard.summary.selling_target})`,
      color: "#f6c85f",
      points: managementDashboard.score_breakdown.map((row) => ({ label: row.month_name, value: row.selling })),
    },
    {
      label: `Close(${managementDashboard.summary.closing_target})`,
      color: "#8b5cf6",
      points: managementDashboard.score_breakdown.map((row) => ({ label: row.month_name, value: row.closing })),
    },
    {
      label: `Prop/RFP(${managementDashboard.summary.prop_rfp_target})`,
      color: "#fb9f3f",
      points: managementDashboard.score_breakdown.map((row) => ({ label: row.month_name, value: row.prop_rfp })),
    },
  ];

  return (
    <div className="space-y-5 portal-page-shell">
      <PageHeader
        eyebrow="Portal dashboard"
        title="Management Dashboard"
        description={`${managementDashboard.summary.completed_calls} completed calls in ${periodLabel} with a median score of ${managementDashboard.summary.median_percent.toFixed(1)}%. Trend tables below cover ${trendRangeLabel}.`}
        serifTitle
        badges={[
          { label: `Snapshot ${periodLabel}`, tone: "neutral" },
          { label: managementDashboard.summary.management_company_name, tone: "brand" },
          { label: `${managementDashboard.summary.report_view_mode} view`, tone: "success" },
        ]}
      />

      <FilterToolbar sticky>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[7rem_7rem_minmax(16rem,1fr)_auto] xl:items-end">
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="select-shell w-full">
              {availableYears.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Month</span>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value === "ytd" ? "ytd" : Number(e.target.value))}
              className="select-shell w-full"
            >
              <option value="ytd">YTD</option>
              {MONTH_NAMES.map((label, index) => (
                <option key={label} value={index + 1}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Management Company</span>
            <select
              value={mcId ?? ""}
              onChange={(e) => setMcId(e.target.value ? Number(e.target.value) : undefined)}
              className="select-shell w-full"
              disabled={isReportScoped}
            >
              <option value="">All HMCs</option>
              {mcList.map((mc) => (
                <option key={mc.id} value={mc.id}>
                  {mc.name}
                </option>
              ))}
            </select>
          </label>

          <button onClick={downloadCsv} className="button-secondary h-11 !px-4 !py-2 text-sm">
            Download CSV
          </button>
        </div>
      </FilterToolbar>

      <MetricStrip
        items={[
          { label: "Completed calls", value: managementDashboard.summary.completed_calls, detail: `Completed in ${periodLabel}`, tone: "brand" },
          { label: "Median score", value: `${managementDashboard.summary.median_percent.toFixed(1)}%`, detail: `${periodLabel} snapshot`, tone: "success" },
          { label: "At-risk calls", value: atRiskCount, detail: "Yellow plus red performance rows", tone: "warning" },
          { label: "Score targets", value: `${managementDashboard.summary.report_view_mode} scoring`, detail: "Targets applied to this dashboard scope", tone: "accent" },
        ]}
      />

      <section className="grid gap-5 xl:grid-cols-2">
        <StatsPanel title="Overall Score">
          <SimpleBarChart data={overallScoreData} color="#dff2d3" />
        </StatsPanel>

        <StatsPanel title="Score Component Trend">
          <MultiLineChart series={componentSeries} />
        </StatsPanel>

        <StatsPanel title="Score Bands">
          <GroupedBandChart data={bandData} />
        </StatsPanel>

        <StatsPanel title={managementDashboard.summary.report_view_mode === "reservation" ? "Reservation Shops" : "Completed Shops"}>
          <SimpleBarChart data={volumeData} color="#fb5b7f" />
        </StatsPanel>
      </section>

      <section className="grid gap-5 xl:grid-cols-3">
        <DataTableShell title="Median % Trend" subtitle={trendLabel}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Month</th>
                <th className="px-4 py-3 text-right">Median %</th>
              </tr>
            </thead>
            <tbody>
              {managementDashboard.percent_by_month.map((row) => (
                <tr key={row.month} className="border-t">
                  <td className="px-4 py-3">{row.month_name}</td>
                  <td className="px-4 py-3 text-right font-semibold">{row.median_percent.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>

        <DataTableShell title="Median Component Trend" subtitle={trendLabel} className="xl:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">Month</th>
                  <th className="px-3 py-3 text-right">Admin</th>
                  <th className="px-3 py-3 text-right">Hosp</th>
                  <th className="px-3 py-3 text-right">Qual</th>
                  <th className="px-3 py-3 text-right">Sell</th>
                  <th className="px-3 py-3 text-right">Close</th>
                  <th className="px-3 py-3 text-right">Prop/RFP</th>
                  <th className="px-3 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {managementDashboard.score_breakdown.map((row) => (
                  <tr key={row.month} className="border-t">
                    <td className="px-3 py-3">{row.month_name}</td>
                    <td className="px-3 py-3 text-right">{row.administration.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.hospitality.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.qualifying.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.selling.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.closing.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right">{row.prop_rfp.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right font-semibold">{row.total_score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataTableShell>
      </section>

      <section className="grid gap-5 xl:grid-cols-[0.7fr,1.3fr]">
        <DataTableShell title="Monthly Volume" subtitle={trendLabel}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Month</th>
                <th className="px-4 py-3 text-right">Calls</th>
              </tr>
            </thead>
            <tbody>
              {managementDashboard.volume_by_month.map((row) => (
                <tr key={row.month} className="border-t">
                  <td className="px-4 py-3">{row.month_name}</td>
                  <td className="px-4 py-3 text-right font-semibold">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>

        <DataTableShell title={`Completed Calls in ${periodLabel}`} subtitle="Presentation-first detail rows for the selected period.">
          <div className="max-h-[34rem] overflow-auto">
            <table className="w-full min-w-[58rem] text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_rgba(148,163,184,0.25)]">
                <tr>
                  <th className="px-3 py-3 text-left">Shop ID</th>
                  <th className="px-3 py-3 text-left">Hotel</th>
                  <th className="px-3 py-3 text-left">Shopper</th>
                  <th className="px-3 py-3 text-left">Sales Staff</th>
                  <th className="px-3 py-3 text-left">Type</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-right">%</th>
                  <th className="px-3 py-3 text-left">Completed</th>
                </tr>
              </thead>
              <tbody>
                {managementDashboard.details.length > 0 ? (
                  managementDashboard.details.map((row) => (
                    <tr key={row.call_id} className="border-t">
                      <td className="px-3 py-3 font-mono text-xs">{row.shop_id}</td>
                      <td className="px-3 py-3">
                        <div className="font-medium">{row.hotel_name}</div>
                        <div className="text-xs text-gray-500">{row.hotel_code}</div>
                      </td>
                      <td className="px-3 py-3">{row.shopper_name || "-"}</td>
                      <td className="px-3 py-3">{row.sales_staff || "-"}</td>
                      <td className="px-3 py-3">{row.call_type}</td>
                      <td className="px-3 py-3">{row.status}</td>
                      <td className="px-3 py-3 text-right font-semibold">{row.percent != null ? `${row.percent}%` : "-"}</td>
                      <td className="px-3 py-3">{formatDashboardDate(row.completed_date) || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr className="border-t">
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                      No completed calls for {periodLabel}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DataTableShell>
      </section>

      <SectionCard title="Score targets" subtitle="Targets applied to the current portal view.">
        <div className="summary-list">
          <div className="summary-row">
            <span>Administration</span>
            <strong>{managementDashboard.summary.administration_target}</strong>
          </div>
          <div className="summary-row">
            <span>Hospitality</span>
            <strong>{managementDashboard.summary.hospitality_target}</strong>
          </div>
          <div className="summary-row">
            <span>Qualifying</span>
            <strong>{managementDashboard.summary.qualifying_target}</strong>
          </div>
          <div className="summary-row">
            <span>Selling</span>
            <strong>{managementDashboard.summary.selling_target}</strong>
          </div>
          <div className="summary-row">
            <span>Closing</span>
            <strong>{managementDashboard.summary.closing_target}</strong>
          </div>
          <div className="summary-row">
            <span>Prop / RFP</span>
            <strong>{managementDashboard.summary.prop_rfp_target}</strong>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
